// Release Plan как данные: что релиз, в какую неделю, недели диапазона по месяцам, фильтры.
// Спека — docs/superpowers/specs/2026-09-10-release-plan-design.md; релизы лонча — 2026-09-11-launch-screen-design.md, «Release Plan»
import { addDays, daysBetween, isoWeek, mondayOf, monthStartOf, monthYearLabel, shortDate, sundayOf, type ISODate } from '@core/dates';
import type { Change } from '@core/store/model';
import type { Task } from '../shared/data';
import type { Planned } from '../shared/apply';
import type { Launch, LaunchTask } from '../../launch/planner';
import { deployTooEarly } from '../shared/deployGuard';
import { planMove } from '../shared/planner';

/** Неделя выхода (понедельник) или null: бэклог и «не деплоится» — не релизы. Прибитая дата или конец работы. */
export function releaseWeekOf(t: Pick<Task, 'in_backlog' | 'deploy' | 'deploy_date' | 'end_date'>): ISODate | null {
  if (t.in_backlog || t.deploy === 'none') return null;
  return mondayOf(t.deploy === 'date' && t.deploy_date ? t.deploy_date : t.end_date);
}

/** Релиз на неделе: задача роадмапа (по releaseWeekOf) или задача лонча со статусом release (по неделе конца). */
export type Release =
  | { kind: 'task'; id: string; title: string; week: ISODate; keyRelease: boolean; projectId: string; task: Task }
  | { kind: 'launch'; id: string; title: string; week: ISODate; keyRelease: false; projectId: null; task: LaunchTask; launch: Launch };
export type LaunchRelease = Extract<Release, { kind: 'launch' }>;

/** Релизы роадмапа и лонча одним списком; задача лонча без лонча (трек удалён между событиями) пропускается. */
export function releasesOf(tasks: Iterable<Task>, launchTasks: Iterable<LaunchTask>, launchOf: (t: LaunchTask) => Launch | undefined): Release[] {
  const out: Release[] = [];
  for (const t of tasks) {
    const week = releaseWeekOf(t);
    if (week) out.push({ kind: 'task', id: t.id, title: t.title, week, keyRelease: t.key_release, projectId: t.project_id, task: t });
  }
  for (const t of launchTasks) {
    if (t.status !== 'release') continue;
    const launch = launchOf(t);
    if (launch) out.push({ kind: 'launch', id: t.id, title: t.title, week: mondayOf(t.end_date), keyRelease: false, projectId: null, task: t, launch });
  }
  return out;
}

export type Week = { monday: ISODate; month: ISODate; number: number; label: string };

/** Понедельник–пятница коротко, как в старом: «14–18 Sep», на стыке месяцев «28 Sep – 2 Oct». */
export const weekLabel = (monday: ISODate): string => {
  const fri = addDays(monday, 4);
  return monday.slice(0, 7) === fri.slice(0, 7) ? `${Number(monday.slice(8))}–${shortDate(fri)}` : `${shortDate(monday)} – ${shortDate(fri)}`;
};

/** Недели диапазона (понедельники); месяц недели — по её понедельнику. */
export function weeksOf(range: { from: ISODate; to: ISODate }): Week[] {
  const out: Week[] = [];
  for (let m = mondayOf(range.from); m <= range.to; m = addDays(m, 7)) {
    out.push({ monday: m, month: monthStartOf(m), number: isoWeek(m), label: weekLabel(m) });
  }
  return out;
}

export type WeekGroup = { week: Week; releases: Release[] };
export type MonthGroup = { month: ISODate; label: string; count: number; weeks: WeekGroup[] };

/** Порядок внутри недели, как в старом: ключевые первыми, потом по порядку проектов (релиз лонча — после всех), потом по названию. */
export const releaseOrder = (projectPosition: Map<string, number>) => (a: Release, b: Release): number =>
  Number(b.keyRelease) - Number(a.keyRelease)
  || (projectPosition.get(a.projectId ?? '') ?? 99) - (projectPosition.get(b.projectId ?? '') ?? 99)
  || a.title.localeCompare(b.title);

/** Релизы диапазона по месяцам и неделям; внутри недели — в порядке входа (см. releaseOrder). */
export function groupByMonth(releases: Iterable<Release>, range: { from: ISODate; to: ISODate }): MonthGroup[] {
  const byWeek = new Map<ISODate, Release[]>();
  for (const r of releases) {
    if (r.week >= mondayOf(range.from) && r.week <= range.to) byWeek.set(r.week, [...(byWeek.get(r.week) ?? []), r]);
  }
  const months = new Map<ISODate, MonthGroup>();
  for (const week of weeksOf(range)) {
    const g = months.get(week.month) ?? { month: week.month, label: monthYearLabel(week.month), count: 0, weeks: [] };
    const list = byWeek.get(week.monday) ?? [];
    g.weeks.push({ week, releases: list });
    g.count += list.length;
    months.set(week.month, g);
  }
  return [...months.values()];
}

export type ReleaseFilter = { query: string; projects: ReadonlySet<string> };
/** Псевдопроект в чипах: релизы лонча при включённых чипах показываются, только если выбран он. */
export const LAUNCH_CHIP = 'launch';

/** Поиск по названию и именам исполнителей (у релиза лонча — по имени лонча); чипы: проект релиза или чип Launch для релиза лонча. */
export function matchesFilter(r: Release, names: string[], f: ReleaseFilter): boolean {
  if (f.projects.size > 0 && !f.projects.has(r.kind === 'launch' ? LAUNCH_CHIP : r.projectId)) return false;
  const q = f.query.trim().toLowerCase();
  if (!q) return true;
  const extra = r.kind === 'launch' ? [r.launch.name] : names;
  return r.title.toLowerCase().includes(q) || extra.some((n) => n.toLowerCase().includes(q));
}

export const isCurrentWeek = (week: Week, today: ISODate): boolean => week.monday <= today && today <= sundayOf(week.monday);

// ---------------------------------------------------------------- ленты Timeline

export type Lane = { id: string; name: string; projectIds: ReadonlySet<string>; showEvents: boolean; showLaunches: boolean };

/** Ленты из таблицы по position; проект без ленты на Timeline не показывается (ленту ставят в карточке проекта). */
export function lanesOf(
  lanes: Iterable<{ id: string; name: string; position: number; show_events: boolean; show_launches: boolean }>,
  links: Iterable<{ lane_id: string; project_id: string }>,
): Lane[] {
  const byLane = new Map<string, Set<string>>();
  for (const l of links) byLane.set(l.lane_id, (byLane.get(l.lane_id) ?? new Set()).add(l.project_id));
  return [...lanes].sort((a, b) => a.position - b.position).map((l) => ({ id: l.id, name: l.name, projectIds: byLane.get(l.id) ?? new Set<string>(), showEvents: l.show_events, showLaunches: l.show_launches }));
}

/** Релизы ленты: задачи её проектов; релизы лонча — если у ленты включён show_launches. */
export const laneReleases = (lane: Lane, releases: Release[]): Release[] =>
  releases.filter((r) => (r.kind === 'launch' ? lane.showLaunches : lane.projectIds.has(r.projectId)));

/** События, пересекающие диапазон — контекст ленты. */
export function eventsInRange<E extends { start_date: ISODate; end_date: ISODate }>(events: Iterable<E>, range: { from: ISODate; to: ISODate }): E[] {
  return [...events].filter((e) => e.start_date <= range.to && e.end_date >= range.from).sort((a, b) => a.start_date.localeCompare(b.start_date));
}

/** Чип бросили на неделю: неделя выхода не раньше последней недели работы — иначе отказ (ограничение в базе). */
export function planDeployWeek(t: Task, monday: ISODate): { error: string } | { set: { deploy: 'date'; deploy_date: ISODate } } {
  if (deployTooEarly(monday, t.end_date)) return { error: `Can't deploy before the work ends (${shortDate(t.end_date)})` };
  return { set: { deploy: 'date', deploy_date: monday } };
}

/**
 * Бросили раньше конца работы и человек выбрал «сдвинуть работу»: задача едет так, чтобы кончиться в ту
 * неделю (тот же planMove, что на сетке, со свободным треком), и дата выхода прибивается к её понедельнику.
 */
export function planShiftToWeek(live: Task, monday: ISODate, rowTasks: Task[]): Planned {
  const start = addDays(live.start_date, daysBetween(mondayOf(live.end_date), monday));
  const moved = planMove(live, { project_id: live.project_id, subproject_id: live.subproject_id, start_date: start, track: live.track ?? 0 }, rowTasks);
  const set = { ...(moved[0] && 'set' in moved[0] ? moved[0].set : {}), deploy: 'date', deploy_date: monday };
  return { changes: [{ table: 'task', id: live.id, set, expect_updated_at: live.updated_at }], op: { kind: 'set_deploy_week', task_id: live.id, shifted: true } };
}

export type LaneMonth = { month: ISODate; weeks: number; releases: number };
/** Полоса месяцев ленты: сколько недель подряд и сколько релизов в каждом; byWeek — релизы по понедельникам. */
export function laneMonths(releases: Release[], range: { from: ISODate; to: ISODate }): { months: LaneMonth[]; byWeek: Map<ISODate, Release[]> } {
  const groups = groupByMonth(releases, range);
  const byWeek = new Map<ISODate, Release[]>();
  for (const g of groups) for (const w of g.weeks) byWeek.set(w.week.monday, w.releases);
  return { months: groups.map((g) => ({ month: g.month, weeks: g.weeks.length, releases: g.count })), byWeek };
}

/** Релизы, которые Timeline реально показывает: попавшие хотя бы в одну ленту, без повторов. */
export const timelineCount = (lanes: Lane[], releases: Release[]): number => new Set(lanes.flatMap((l) => laneReleases(l, releases).map((r) => r.id))).size;

// ---------------------------------------------------------------- редактор лент

export type LaneDraft = { id: string | null; name: string; projectIds: string[]; showEvents: boolean; showLaunches: boolean };
type LaneRow = { id: string; name: string; position: number; show_events: boolean; show_launches: boolean; updated_at: string };
type LinkRow = { id: string; lane_id: string; project_id: string };

/** Что записать после правки лент: ленты по порядку черновика, связи проект–лента разницей, удалённые — каскадом. */
export function planLanesSave(draft: LaneDraft[], lanes: LaneRow[], links: LinkRow[], newId: () => string): { error: string } | { changes: Change[] } {
  const names = draft.map((d) => d.name.trim());
  if (names.some((n) => !n)) return { error: 'Every lane needs a name' };
  if (new Set(names.map((n) => n.toLowerCase())).size !== names.length) return { error: 'Lane names must differ' };
  const changes: Change[] = [];
  const keep = new Set(draft.map((d) => d.id).filter(Boolean));
  for (const l of lanes) if (!keep.has(l.id)) changes.push({ table: 'timeline_lane', id: l.id, delete: true });
  draft.forEach((d, position) => {
    const name = d.name.trim();
    const id = d.id ?? newId();
    const was = d.id ? lanes.find((l) => l.id === d.id) : undefined;
    const fields = { name, position, show_events: d.showEvents, show_launches: d.showLaunches };
    if (!was) changes.push({ table: 'timeline_lane', insert: { id, ...fields } });
    else if (was.name !== name || was.position !== position || was.show_events !== d.showEvents || was.show_launches !== d.showLaunches) {
      changes.push({ table: 'timeline_lane', id, set: fields, expect_updated_at: was.updated_at });
    }
    const mine = was ? links.filter((x) => x.lane_id === id) : [];
    for (const x of mine) if (!d.projectIds.includes(x.project_id)) changes.push({ table: 'lane_project', id: x.id, delete: true });
    for (const p of d.projectIds) if (!mine.some((x) => x.project_id === p)) changes.push({ table: 'lane_project', insert: { lane_id: id, project_id: p } });
  });
  return { changes };
}

// ---------------------------------------------------------------- оттенки месяцев

// Набор старого Timeline, привязан к календарному месяцу (месяц % 6): сентябрь всегда зелёный, октябрь розовый.
const MONTH_TINTS = [
  { bg: '#EDEBFC', fg: '#4C3FB8' }, { bg: '#FFF7EC', fg: '#9A5B00' }, { bg: '#EAF8F1', fg: '#187741' },
  { bg: '#FDEEED', fg: '#B03A2E' }, { bg: '#E5F3FF', fg: '#1F5F9A' }, { bg: '#FBF0E0', fg: '#8A5A1A' },
];
export const monthTint = (month: ISODate): { bg: string; fg: string } => MONTH_TINTS[(Number(month.slice(5, 7)) - 1) % MONTH_TINTS.length]!;
